import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  centavos,
  diasEntre,
  formatMoney,
  hoy,
  newId,
  parseMoney,
  subMoney,
  type Centavos,
} from '@abastece/shared';
import type { Abono, Cliente, Incobrable } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { sesionAbierta } from '../lib/caja.js';
import { emitir, emitirYVer } from '../lib/emitir.js';
import {
  debeDesde,
  estadoDeCuenta,
  textoEstadoDeCuenta,
  textoRecordatorio,
  type Movimiento,
} from '../lib/fiado.js';
import { imprimirTexto } from '../lib/imprimir.js';
import { datosNegocio, enlaceWhatsApp } from '../lib/recibo.js';
import { ordenarListas } from '../lib/catalogo.js';
import { usePuede, useSesion } from '../lib/sesion.js';
import { volver } from '../lib/volver.js';
import { Campo } from '../ui/Campo.js';
import {
  CLIENTE_VACIO,
  DIAS,
  FormularioCliente,
  borradorDe,
  camposDe,
  saldoAnteriorDe,
} from '../ui/FormularioCliente.js';
import { Chip, Ico } from '../ui/Iconos.js';
import { AbonoModal } from '../ui/Abono.js';
import { Modal } from '../ui/Modal.js';
import { VerFotoCedula } from '../ui/FotoCedula.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { PedirPin, usePinParaAnular } from '../ui/PedirPin.js';
import { Confirmar } from '../ui/Preguntar.js';
import { useT, type Clave } from '../i18n/index.js';
import { DetalleVenta } from './Ventas.js';

/**
 * Un cliente: lo que debe y cómo llegó a deberlo.
 *
 * The balance at the top is the reducers' figure, the one the books agree with. Under it, the
 * notebook: every fiado, abono and return that went on the account, newest first, each with
 * what was owed once it was counted. From here the client pays something off, gets their
 * statement on WhatsApp or on paper, or a friendly reminder — and the owner sets how much they
 * may owe, and gives up on what will never be paid.
 *
 * A fiado, or a return off the account, opens its sale the way «Historial de ventas» opens it
 * (the owner, 2026-10-22: «Each entry in history should also be clickable and show that particular
 * order with all the same functions as in sales history»): the receipt to send or print again, a
 * return, a void, each asking what it asks there.
 */
/**
 * How many lines of a client's history show before «Ver anteriores». A neighbour who takes fiado
 * every day had 1,600 of them after three months, and drawing them all held the page for over
 * half a second (AUDITORIAS.md item 2). The statement sent or printed was already the last 15.
 */
const A_LA_VISTA = 50;

export function ClienteDetalle() {
  const t = useT();
  const { clienteId } = useParams({ from: '/clientes/$clienteId' });
  // null: there is no such client; undefined: Dexie has not answered yet.
  const cliente = useLiveQuery(async () => (await db.clientes.get(clienteId)) ?? null, [clienteId], undefined);
  const ventas = useLiveQuery(() => db.ventas.where('clienteId').equals(clienteId).toArray(), [clienteId], []);
  const abonos = useLiveQuery(() => db.abonos.where('clienteId').equals(clienteId).toArray(), [clienteId], []);
  const devoluciones = useLiveQuery(
    () => db.devoluciones.where('clienteId').equals(clienteId).toArray(),
    [clienteId],
    [],
  );
  const incobrables = useLiveQuery(() => db.incobrables.where('clienteId').equals(clienteId).toArray(), [clienteId], []);
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);

  const puede = usePuede('gestionar_clientes');
  const puedeDarCredito = usePuede('dar_credito');
  const puedeElegirLista = usePuede('elegir_lista');
  const puedeCobrar = usePuede('vender');
  const puedeAnular = usePuede('anular_venta');

  const [editando, setEditando] = useState(false);
  const [b, setB] = useState(CLIENTE_VACIO);
  const [desactivando, setDesactivando] = useState(false);
  const [abonando, setAbonando] = useState(false);
  const [aLaVista, setALaVista] = useState(A_LA_VISTA);
  /** The sale a line opened, by id: the sheet reads it live, so a return shows the moment it is made. */
  const [abierta, setAbierta] = useState<string | null>(null);
  const [anulando, setAnulando] = useState<Abono | null>(null);
  const [perdiendo, setPerdiendo] = useState(false);
  const [deshaciendo, setDeshaciendo] = useState<{ incobrable: Incobrable; sigue: Centavos } | null>(null);

  if (cliente === undefined) return null;
  if (cliente === null) {
    return (
      <Pagina titulo={t('clientes.titulo')} volverA="/clientes">
        <Vacio mensaje={t('cuenta.noExiste')} icono="clientes" />
      </Pagina>
    );
  }

  const activas = ordenarListas(listas);
  const saldo = cliente.saldo ?? (0 as Centavos);
  const movimientos = estadoDeCuenta(ventas, abonos, devoluciones, cliente, incobrables);
  const desde = debeDesde(movimientos);
  const negocio = config.negocioNombre;

  async function guardar() {
    await emitir('cliente.actualizado', {
      clienteId,
      ...camposDe(b, { limite: puedeDarCredito, lista: puedeElegirLista, cedula: puedeDarCredito }),
    });
    // Only when it changed: the same figure again is an event that does nothing.
    const anterior = saldoAnteriorDe(b);
    if (puedeDarCredito && anterior !== null && anterior !== (cliente?.saldoInicial ?? 0)) {
      await emitir('cliente.saldo_inicial', { clienteId, monto: anterior });
    }
    setEditando(false);
  }

  return (
    <Pagina
      titulo={cliente.nombre}
      subtitulo={[
        t(cliente.tipo === 'mayorista' ? 'clientes.etiquetaMayorista' : 'clientes.etiquetaMostrador'),
        activas.find((l) => l.id === cliente.listaId)?.nombre,
        cliente.diaVisita !== null ? t(DIAS[cliente.diaVisita]!) : null,
        cliente.telefono,
      ]
        .filter(Boolean)
        .join(' · ')}
      volverA="/clientes"
      acciones={
        puede && (
          <button
            type="button"
            className="btn-secundario gap-1.5 px-4 text-sm"
            onClick={() => {
              setB(borradorDe(cliente));
              setEditando(true);
            }}
          >
            <Ico icono="editar" className="size-4" />
            {t('cuenta.editar')}
          </button>
        )
      }
    >
      <section className={`tarjeta mb-4 flex flex-col gap-4 ${saldo > 0 ? 'ring-amber-500/25' : ''}`}>
        <div className="flex items-center gap-4">
          <Chip icono="fiado" tinte={saldo > 0 ? 'ambar' : 'neutro'} />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-slate-400">
              {t(saldo === 0 ? 'cuenta.noDebe' : saldo > 0 ? 'cuenta.debe' : 'cuenta.aFavor')}
            </p>
            {saldo !== 0 && (
              <p className="tabular text-3xl font-bold">{formatMoney(centavos(Math.abs(saldo)))}</p>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1 text-xs text-slate-400">
          <p>
            {cliente.limiteCredito === null || cliente.limiteCredito === undefined
              ? t('cuenta.sinFiado')
              : t('cuenta.limite', { monto: formatMoney(cliente.limiteCredito) })}
          </p>
          {saldo > 0 && desde && (
            <p className={diasEntre(desde, hoy()) > 30 ? 'text-amber-300' : ''}>
              {diasEntre(desde, hoy()) === 0
                ? t('cuenta.debeDesdeHoy')
                : t('cuenta.debeDesde', { fecha: desde, n: diasEntre(desde, hoy()) })}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {puedeCobrar && saldo > 0 && (
            <button type="button" className="btn-primario flex-1 basis-40" onClick={() => setAbonando(true)}>
              <Ico icono="cobrar" />
              {t('comun.abonar')}
            </button>
          )}
          {saldo > 0 && (
            <a
              className="btn-secundario flex-1 basis-40 text-sm"
              href={enlaceWhatsApp(
                textoRecordatorio(negocio, cliente.nombre, saldo),
                cliente.telefono ?? undefined,
              )}
              target="_blank"
              rel="noreferrer"
            >
              <Ico icono="whatsapp" />
              {t('cuenta.recordar')}
            </a>
          )}
        </div>
        {/* What will never be paid: the owner's to give up on, into an expense of its own. */}
        {puedeDarCredito && saldo > 0 && (
          <button
            type="button"
            className="btn-texto -mt-2 gap-1 self-start text-xs text-slate-400"
            onClick={() => setPerdiendo(true)}
          >
            <Ico icono="desactivar" className="size-3.5" />
            {t('cuenta.darPorPerdido')}
          </button>
        )}
        {/* The statement: every movement and the balance, to send or to hand over on paper. */}
        {movimientos.length > 0 && (
          <div className="flex flex-col gap-2 border-t border-white/5 pt-3">
            <span className="text-xs text-slate-400">{t('cuenta.estado')}</span>
            <div className="flex gap-2">
              <a
                className="btn-secundario h-11 min-h-0 min-w-0 flex-1 gap-1.5 px-3 text-sm"
                href={enlaceWhatsApp(
                  textoEstadoDeCuenta(negocio, cliente, movimientos),
                  cliente.telefono ?? undefined,
                )}
                target="_blank"
                rel="noreferrer"
              >
                <Ico icono="whatsapp" className="size-4 shrink-0" />
                WhatsApp
              </a>
              <button
                type="button"
                className="btn-secundario h-11 min-h-0 min-w-0 flex-1 gap-1.5 px-3 text-sm"
                onClick={() => imprimirTexto(textoEstadoDeCuenta(negocio, cliente, movimientos))}
              >
                <Ico icono="imprimir" className="size-4 shrink-0" />
                {t('comun.imprimir')}
              </button>
            </div>
          </div>
        )}
      </section>

      <h2 className="rotulo">{t('cuenta.movimientos')}</h2>
      {movimientos.length === 0 ? (
        <Vacio mensaje={t('cuenta.sinMovimientos')} icono="fiado" />
      ) : (
        <>
          <ul className="tarjeta flex flex-col divide-y divide-slate-800 py-1">
            {movimientos
              .slice(-aLaVista)
              .reverse()
              .map((m) => (
                <FilaMovimiento
                  key={`${m.tipo}:${m.id}`}
                  m={m}
                  onAbrir={m.ventaId ? () => setAbierta(m.ventaId!) : undefined}
                  onAnular={
                    puedeAnular && m.abono
                      ? () => setAnulando(m.abono!)
                      : puedeDarCredito && m.incobrable
                        ? () => setDeshaciendo({ incobrable: m.incobrable!, sigue: -m.monto as Centavos })
                        : undefined
                  }
                />
              ))}
          </ul>
          {movimientos.length > aLaVista && (
            <button
              type="button"
              className="btn-texto mt-2 w-full gap-1 text-sm text-slate-400"
              onClick={() => setALaVista((n) => n + A_LA_VISTA * 2)}
            >
              <Ico icono="desplegar" className="size-4" />
              {t('cuenta.verAnteriores', { n: movimientos.length - aLaVista })}
            </button>
          )}
        </>
      )}

      {/* Where to find them, and — for the owner, who decides who gets fiado — who they are. */}
      {(cliente.direccion || (puedeDarCredito && (cliente.cedula || cliente.fotoCedula))) && (
        <>
          <h2 className="rotulo mt-6">{t('cuenta.datos')}</h2>
          <section className="tarjeta flex flex-col gap-4">
            {cliente.direccion && (
              <div>
                <span className="etiqueta">{t('comun.direccion')}</span>
                <p className="text-sm">{cliente.direccion}</p>
              </div>
            )}
            {puedeDarCredito && cliente.cedula && (
              <div>
                <span className="etiqueta">{t('clientes.cedula')}</span>
                <p className="tabular text-sm">{cliente.cedula}</p>
              </div>
            )}
            {puedeDarCredito && cliente.fotoCedula && (
              <div className="flex flex-col gap-1">
                <span className="etiqueta">{t('clientes.fotoCedula')}</span>
                <VerFotoCedula huella={cliente.fotoCedula} />
              </div>
            )}
          </section>
        </>
      )}

      {abierta && <DetalleVenta ventaId={abierta} negocio={datosNegocio(config)} onCerrar={() => setAbierta(null)} />}

      {abonando && (
        <AbonoModal cliente={cliente} negocio={negocio} onCerrar={() => setAbonando(false)} />
      )}

      {anulando && (
        <AnularAbono abono={anulando} cliente={cliente} onCerrar={() => setAnulando(null)} />
      )}

      {perdiendo && <DarPorPerdido cliente={cliente} onCerrar={() => setPerdiendo(false)} />}

      {deshaciendo && <DeshacerPerdido {...deshaciendo} onCerrar={() => setDeshaciendo(null)} />}

      {/* Its own sheet, the account as it was behind it: forms in a sheet, not above the rest (the owner, 2026-10-06). */}
      {editando && (
        <Modal titulo={t('clientes.editarTitulo')} onCerrar={() => setEditando(false)}>
          <FormularioCliente
            b={b}
            setB={setB}
            listas={activas}
            puedeDarCredito={puedeDarCredito}
            puedeElegirLista={puedeElegirLista}
            conCedula={puedeDarCredito}
            enHoja
            onGuardar={() => void guardar()}
            extra={
              <>
                <button type="button" className="btn-secundario px-4" onClick={() => setEditando(false)}>
                  <Ico icono="cancelar" />
                  {t('comun.cancelar')}
                </button>
                <button type="button" className="btn-secundario px-4" onClick={() => setDesactivando(true)}>
                  <Ico icono="desactivar" />
                  {t('clientes.desactivar')}
                </button>
              </>
            }
          />
        </Modal>
      )}

      {desactivando && (
        <Confirmar
          titulo={t('preguntar.desactivarCliente')}
          mensaje={t('clientes.confirmarDesactivar')}
          confirmar={t('preguntar.desactivar')}
          icono="desactivar"
          peligro
          onCerrar={() => setDesactivando(false)}
          onConfirmar={() => {
            void emitir('cliente.desactivado', { clienteId });
            // Back where the client was opened from: the clients, «Fiado», «Análisis».
            void volver({ to: '/clientes' });
          }}
        />
      )}
    </Pagina>
  );
}

function FilaMovimiento({
  m,
  onAbrir,
  onAnular,
}: {
  m: Movimiento;
  /** A fiado or a return: the whole line opens its sale. Never with `onAnular`, a button inside a button. */
  onAbrir?: () => void;
  onAnular?: () => void;
}) {
  const t = useT();
  const concepto =
    m.tipo === 'inicial'
      ? t('cuenta.saldoAnterior')
      : m.tipo === 'fiado'
        ? t('cuenta.fiado', { ticket: m.ticket ?? '' })
        : m.tipo === 'devolucion'
          ? t('cuenta.devolucion', { ticket: m.ticket ?? '' })
          : m.tipo === 'perdido'
            ? t('cuenta.perdido')
            : t('cuenta.abono', { forma: t(`pago.${m.abono!.formaPago}` as Clave) });
  /*
   * Wrapped, never cut: at 320 px the amounts beside it left «Devolución · PR-000006» 115 of the 167
   * px it needs, and truncating it hid the ticket, the one thing that says which sale it was. The
   * ticket itself stays in one piece.
   */
  const i = m.ticket ? concepto.indexOf(m.ticket) : -1;
  const contenido = (
    <>
      <div className="min-w-0 flex-1">
        <p className="text-sm">
          {i < 0 ? (
            concepto
          ) : (
            <>
              {concepto.slice(0, i)}
              <span className="whitespace-nowrap">{m.ticket}</span>
              {concepto.slice(i + m.ticket!.length)}
            </>
          )}
        </p>
        <p className="tabular mt-0.5 truncate text-xs text-slate-500">
          {m.fecha}
          {m.abono?.estadoPago === 'pendiente' ? ` · ${t('cuenta.porConfirmar')}` : ''}
          {m.abono?.nota ? ` · ${m.abono.nota}` : ''}
          {m.incobrable ? ` · ${m.incobrable.motivo}` : ''}
        </p>
        {onAnular && (
          <button type="button" className="btn-texto mt-0.5 gap-1 text-xs text-slate-500" onClick={onAnular}>
            <Ico icono="anular" className="size-3.5" />
            {t(m.tipo === 'perdido' ? 'cuenta.deshacer' : 'comun.anular')}
          </button>
        )}
      </div>
      <div className="shrink-0 text-right">
        <p className={`tabular text-sm font-medium ${m.monto > 0 ? 'text-amber-200' : 'text-emerald-300'}`}>
          {formatMoney(m.monto, { signed: true })}
        </p>
        <p className="tabular mt-0.5 text-xs text-slate-500">
          {t('cuenta.queda', { monto: formatMoney(m.saldo) })}
        </p>
      </div>
    </>
  );
  // Lighting under the mouse a little wider than its words, as the rows of «Efectivo» do.
  if (onAbrir) {
    return (
      <li className="px-1">
        <button
          type="button"
          onClick={onAbrir}
          data-sin-icono="fila"
          data-movimiento-venta={m.ventaId}
          className="-mx-2 flex w-[calc(100%+1rem)] items-start gap-3 rounded-xl px-2 py-2.5 text-left transition hover:bg-white/5"
        >
          {contenido}
        </button>
      </li>
    );
  }
  return <li className="flex items-start gap-3 px-1 py-2.5">{contenido}</li>;
}

/** An abono put down by mistake. It asks for a PIN the way a void does (`usePinParaAnular`). */
function AnularAbono({
  abono,
  cliente,
  onCerrar,
}: {
  abono: Abono;
  cliente: Cliente;
  onCerrar: () => void;
}) {
  const t = useT();
  const sesion = useSesion();
  const [motivo, setMotivo] = useState('');
  const [pidiendoPin, setPidiendoPin] = useState(false);
  /*
   * An abono that went into a till which has since closed: its money went to general cash with the
   * corte, so the cash comes back out of the till open on this device. Null for "none"; undefined
   * while still being looked up.
   */
  const turno = useLiveQuery(
    async () => (abono.sesionId ? ((await db.sesiones.get(abono.sesionId)) ?? null) : null),
    [abono.sesionId],
  );
  const caja = useLiveQuery(async () => (await sesionAbierta()) ?? null, []);
  const deOtraCaja = abono.sesionId !== null && turno !== undefined && turno?.estado !== 'abierta';
  // From before this shift, the owner's PIN: a till since closed, or with no till, another day.
  const pin = usePinParaAnular(abono.sesionId !== null ? turno?.estado !== 'abierta' : abono.fecha !== hoy());

  return (
    <Modal titulo={t('abono.anular')} onCerrar={onCerrar}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-400">
          {t('abono.anularExplicacion', { monto: formatMoney(abono.monto), fecha: abono.fecha })}
        </p>
        {deOtraCaja && caja !== undefined && (
          <p className={`text-xs ${caja ? 'text-slate-400' : 'text-amber-300'}`}>
            {t(caja ? 'abono.anularDeCaja' : 'abono.anularSinCaja', { monto: formatMoney(abono.monto) })}
          </p>
        )}
        <Campo
          maxLength={200}
          etiqueta={t('comun.motivo')}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          autoFocus
          autoComplete="off"
        />
        <button
          type="button"
          className="btn-peligro"
          disabled={!motivo.trim() || (deOtraCaja && !caja)}
          onClick={() => setPidiendoPin(true)}
        >
          <Ico icono="anular" />
          {t('abono.anular')}
        </button>
      </div>
      {pidiendoPin && (
        <PedirPin
          titulo={pin.titulo}
          motivo={t(
            pin.porTurno ? 'abono.duenoOtroTurno' : pin.delDueno ? 'abono.duenoParaAnular' : 'abono.pinParaAnular',
            { monto: formatMoney(abono.monto), cliente: cliente.nombre, nombre: sesion?.nombre ?? '' },
          )}
          verificar={pin.verificar}
          donde={pin.donde}
          onCancelar={() => setPidiendoPin(false)}
          onAutorizado={(usuarioId) => {
            setPidiendoPin(false);
            void emitir('abono.anulado', {
              abonoId: abono.id,
              motivo: motivo.trim(),
              ...(pin.delDueno ? { autorizadoPor: usuarioId } : {}),
              ...(deOtraCaja && caja ? { sesionId: caja.id } : {}),
            }).then(onCerrar);
          }}
        />
      )}
    </Modal>
  );
}

/**
 * Giving up on what a client owes: they moved away, they died, they will not pay. It comes off
 * what they owe and goes down as «Fiado incobrable», and the sale stays a sale. The owner's alone,
 * and never more than is owed; undone if they come back.
 */
function DarPorPerdido({ cliente, onCerrar }: { cliente: Cliente; onCerrar: () => void }) {
  const t = useT();
  const debe = cliente.saldo ?? (0 as Centavos);
  const [monto, setMonto] = useState(String(debe / 100));
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valor = parseMoney(monto);
  const demas = valor !== null && valor > debe;
  const valido = valor !== null && valor > 0 && !demas && motivo.trim() !== '';

  async function dar() {
    if (!valido || ocupado) return;
    setOcupado(true);
    const { rechazo } = await emitirYVer('incobrable.registrado', {
      incobrableId: newId(),
      clienteId: cliente.id,
      fecha: hoy(),
      monto: valor,
      motivo: motivo.trim(),
    });
    if (rechazo) {
      setOcupado(false);
      setError(rechazo);
      return;
    }
    onCerrar();
  }

  return (
    <Modal titulo={t('perdido.titulo')} onCerrar={onCerrar}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-400">
          {t('perdido.explicacion', { nombre: cliente.nombre, monto: formatMoney(debe) })}
        </p>
        <Campo
          etiqueta={t('perdido.monto')}
          inputMode="decimal"
          className="tabular text-lg"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          error={demas ? t('perdido.masDeLoQueDebe', { monto: formatMoney(debe) }) : undefined}
          ayuda={
            valor !== null && valor > 0 && !demas && debe > valor
              ? t('perdido.quedaria', { monto: formatMoney(subMoney(debe, valor)) })
              : undefined
          }
        />
        <Campo
          maxLength={200}
          etiqueta={t('perdido.motivo')}
          placeholder={t('perdido.motivoEjemplo')}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          autoComplete="off"
        />
        {cliente.limiteCredito != null && (
          <p className="text-xs text-slate-400">{t('perdido.limite', { monto: formatMoney(cliente.limiteCredito) })}</p>
        )}
        {error && <p className="text-sm text-rose-400">{error}</p>}
        <button type="button" className="btn-peligro" disabled={!valido || ocupado} onClick={() => void dar()}>
          <Ico icono="desactivar" />
          {ocupado ? t('comun.guardando') : t('perdido.confirmar')}
        </button>
      </div>
    </Modal>
  );
}

/**
 * It was a mistake, or they will pay after all: they owe it again — what still stands of it
 * (`perdidoQueSigue`), since what came back already is not owed twice.
 */
function DeshacerPerdido({ incobrable, sigue, onCerrar }: { incobrable: Incobrable; sigue: Centavos; onCerrar: () => void }) {
  const t = useT();
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function deshacer() {
    const { rechazo } = await emitirYVer('incobrable.anulado', { incobrableId: incobrable.id, motivo: motivo.trim() });
    if (rechazo) setError(rechazo);
    else onCerrar();
  }

  return (
    <Modal titulo={t('perdido.deshacerTitulo')} onCerrar={onCerrar}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-slate-400">
          {sigue < incobrable.monto
            ? t('perdido.deshacerParte', { monto: formatMoney(sigue), total: formatMoney(incobrable.monto), fecha: incobrable.fecha })
            : t('perdido.deshacerExplicacion', { monto: formatMoney(incobrable.monto), fecha: incobrable.fecha })}
        </p>
        <Campo
          maxLength={200}
          etiqueta={t('comun.motivo')}
          placeholder={t('perdido.deshacerEjemplo')}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          autoFocus
          autoComplete="off"
        />
        {error && <p className="text-sm text-rose-400">{error}</p>}
        <button type="button" className="btn-primario" disabled={!motivo.trim()} onClick={() => void deshacer()}>
          <Ico icono="anular" />
          {t('cuenta.deshacer')}
        </button>
      </div>
    </Modal>
  );
}
